// Client-side .hcloud encryption (item 2).
//
// PBKDF2 (SHA-256) derives an AES-GCM key from the encryption password; the
// salt and IV are random per file and stored in the .hcloud container so the
// file is self-describing. The encryption password itself is never stored on
// the server - only an Argon2id verifier lives there (see internal/auth).
//
// Uses @noble/* so encryption works on plain HTTP (Web Crypto subtle is
// secure-context only). Container layout matches files encrypted via subtle.
//
// Container layout:  "HCLOUD1" | salt(16) | iv(12) | AES-GCM ciphertext+tag
import { gcm } from '@noble/ciphers/aes.js';
import { pbkdf2 } from '@noble/hashes/pbkdf2';
import { sha256 } from '@noble/hashes/sha2';
import { utf8ToBytes } from '@noble/hashes/utils';

const MAGIC = new TextEncoder().encode('HCLOUD1');
const SALT_LEN = 16;
const IV_LEN = 12;
const PBKDF2_ITER = 150_000;

function randomBytes(len: number): Uint8Array {
  const out = new Uint8Array(len);
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error('Cannot generate encryption material (crypto unavailable)');
  }
  globalThis.crypto.getRandomValues(out);
  return out;
}

function deriveKeyBytes(password: string, salt: Uint8Array): Uint8Array {
  return pbkdf2(sha256, utf8ToBytes(password), salt, { c: PBKDF2_ITER, dkLen: 32 });
}

/** Encrypts a File into a self-describing .hcloud File. */
export async function encryptFile(file: File, password: string): Promise<File> {
  const salt = randomBytes(SALT_LEN);
  const iv = randomBytes(IV_LEN);
  const key = deriveKeyBytes(password, salt);
  const plain = new Uint8Array(await file.arrayBuffer());
  const cipher = gcm(key, iv).encrypt(plain);
  const out = new Uint8Array(MAGIC.length + SALT_LEN + IV_LEN + cipher.length);
  out.set(MAGIC, 0);
  out.set(salt, MAGIC.length);
  out.set(iv, MAGIC.length + SALT_LEN);
  out.set(cipher, MAGIC.length + SALT_LEN + IV_LEN);
  return new File([out], `${file.name}.hcloud`, { type: 'application/octet-stream' });
}

/** Decrypts a .hcloud File back to its original bytes. */
export async function decryptFile(file: File, password: string): Promise<File> {
  const buf = new Uint8Array(await file.arrayBuffer());
  if (buf.length < MAGIC.length + SALT_LEN + IV_LEN + 16) {
    throw new Error('not a .hcloud file');
  }
  for (let i = 0; i < MAGIC.length; i++) {
    if (buf[i] !== MAGIC[i]) throw new Error('not a .hcloud file');
  }
  const salt = buf.slice(MAGIC.length, MAGIC.length + SALT_LEN);
  const iv = buf.slice(MAGIC.length + SALT_LEN, MAGIC.length + SALT_LEN + IV_LEN);
  const cipher = buf.slice(MAGIC.length + SALT_LEN + IV_LEN);
  const key = deriveKeyBytes(password, salt);
  let plain: Uint8Array;
  try {
    plain = gcm(key, iv).decrypt(cipher);
  } catch {
    throw new Error('decryption failed (wrong password or corrupted file)');
  }
  const name = file.name.replace(/\.hcloud$/i, '');
  const bytes = new Uint8Array(plain);
  return new File([bytes], name, { type: 'application/octet-stream' });
}

/** True when the file name is a .hcloud container. */
export function isHcloud(name: string): boolean {
  return /\.hcloud$/i.test(name);
}
